import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    OnInit,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {MenuCategoryData, MenuExtraGroupData, MenuItemData, MenuPlgSellableData} from '../../../menu/menu-entity.model';
import {MenuItemSize, MenuModel} from '../../../menu/menu.model';
import {ResponsiveDirective} from "../../../responsive/responsive.directive";
import {ResponsiveService} from "../../../responsive/responsive.service";
import {NavigationService} from "../../../navigation/navigation.service";
import {NotificationService} from "../../../notification/notification.service";
import {ButtonComponent} from "../../../buttons/button/button.component";
import {ButtonProminence, ButtonTier} from "../../../buttons/button/button";
import {LinkComponent} from "../../../buttons/link/link.component";
import {
    OrderItemOptionControlComponent,
    OrderItemOptionLayout
} from "./order-item-option-control/order-item-option-control.component";
import {PromoModel} from '../../../promo/promo.model';
import {OrderingContextModel} from '../../../ordering-context/ordering-context.model';
import {OrderCompositeItem, OrderItem, OrderPlgItem} from '../../order';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {OrderItemExtrasEditPanelComponent} from "./order-item-extras-edit-panel/order-item-extras-edit-panel.component";
import {DietaryPanelComponent} from "../dietary-panel/dietary-panel.component";
import {ImageCacheDirective} from '../../../ui-elements/image/image-cache.directive';
import {OrderModel} from '../../order.model';
import {CurrencyPipe} from '../../../utils/currency.pipe';
import {HtmlPipe} from '../../../utils/html.pipe';
import {ConfigurationModel} from '../../../configuration/configuration.model';
import {ActivatableDirective} from '../../../ui-elements/activatable.directive';

@Component({
    selector: "woa-order-item-options-panel",
    templateUrl: "./order-item-options-panel.component.html",
    styleUrls: ["./order-item-options-panel.component.scss"],
    host: {
        "[class.empty]": "isEmpty()"
    },
    imports: [
        ButtonComponent,
        CurrencyPipe,
        DietaryPanelComponent,
        HtmlPipe,
        ImageCacheDirective,
        LinkComponent,
        NgClass,
        OrderItemExtrasEditPanelComponent,
        OrderItemOptionControlComponent,
        ResponsiveDirective,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemOptionsPanelComponent implements OnInit {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public orderModel: OrderModel = inject(OrderModel);
    public menuModel: MenuModel = inject(MenuModel);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    private promoModel: PromoModel = inject(PromoModel);
    private notificationService: NotificationService = inject(NotificationService);
    private navigationService: NavigationService = inject(NavigationService);

    public readonly orderItem: InputSignal<OrderItem> = input.required<OrderItem>();
    public readonly originalOrderItem: InputSignal<OrderItem> = input.required<OrderItem>();
    public readonly category: InputSignal<MenuCategoryData> = input.required<MenuCategoryData>();

    public readonly showSizes: InputSignal<boolean> = input<boolean>(true);
    public readonly showOptions: InputSignal<boolean> = input<boolean>(true);

    public onSizeSelect: OutputEmitterRef<MenuItemData> = output<MenuItemData>();
    public onExtraChange: OutputEmitterRef<MenuExtraGroupData> = output<MenuExtraGroupData>();

    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    OrderItemOptionLayout = OrderItemOptionLayout;
    OrderModel = OrderModel;

    public menuItemSizes: WritableSignal<MenuItemSize[] | undefined> = signal<MenuItemSize[] | undefined>(undefined);
    public plgHasImages: WritableSignal<Map<string, boolean>> = signal(new Map());
    /**
     * Contains the list of plg sellables that will show to the user.
     * It filters based on the menu item settings for the plg item.
     */
    public availablePlgSellables: WritableSignal<Map<string, MenuPlgSellableData[]>> = signal(new Map());

    public selectedSize: WritableSignal<MenuItemSize> = signal<MenuItemSize>(undefined);

    public isEmpty: Signal<boolean> = computed(() => !this.canShowSizes() && !this.showOptions());
    public canShowSizes: Signal<boolean> = computed(() => this.showSizes() && this.orderItem().menuItem().sellableSizeGroup && this.menuItemSizes()?.length >= 2);
    public isCompositeItem: Signal<boolean> = computed(() => this.orderItem() instanceof OrderCompositeItem);
    public orderCompositeItem: Signal<OrderCompositeItem | undefined> = computed(() => this.isCompositeItem() ? <OrderCompositeItem>this.orderItem() : undefined);
    public sizesTitle: Signal<string | undefined> = computed(() => this.menuItemSizes() && this.menuItemSizes().length ? (this.menuItemSizes()[0].sellableSize.externalSizeName ? "woa.heading.order.sizes.select" : "woa.heading.order.sizes.options") : undefined);
    public showSinglePlgItem: Signal<boolean> = computed<boolean>(() => this.responsiveService.mobile());

    public readonly plgSellableAtLimit: Signal<(plgSellable: MenuPlgSellableData) => boolean> = computed(() => {
        const orderItemQuantity: number = this.orderItem().quantitySignal();
        const sellableAtLimit: (sellableId: number, quantum: number, excludedQty: number) => boolean = this.orderModel.sellableAtLimit();
        const sellableQtyOrdered: (sellableId: number) => number = this.orderItem().sellableQtyOrdered();
        return (plgSellable: MenuPlgSellableData): boolean =>
            sellableAtLimit(plgSellable.menuItem ? plgSellable.menuItem.sellable.sellableId : plgSellable.sellable.sellableId,
                orderItemQuantity, sellableQtyOrdered(plgSellable.sellable.sellableId));
    });

    public minimalMode: boolean;
    private originalSelectedSize: MenuItemSize;

    constructor() {
        effect(() => {
            if (this.isCompositeItem()) {
                this.availablePlgSellables.set(new Map());
                this.plgHasImages.set(new Map());
                this.orderCompositeItem()!.compositeComponentsSignal().forEach((componentItem: OrderItem) => {
                    this.availablePlgSellables().set(componentItem.identifier, []);
                    if (componentItem instanceof OrderPlgItem) {
                        const plgItems: MenuPlgSellableData[] = [];
                        componentItem.plg.sellableLines.forEach((plgItem: MenuPlgSellableData) => {
                            if (!this.menuModel.plgItemIsAvailable()(plgItem.menuItem, this.orderCompositeItem()!.menuItem(), this.orderItem() ? this.orderItem().selectedFilterTags() : undefined)) {
                                return;
                            }
                            if (plgItem.menuItem && !!this.menuModel.standardItemImage()(plgItem.menuItem)) {
                                this.plgHasImages().set(componentItem.identifier, true);
                            }
                            plgItems.push(plgItem);
                        });
                        this.availablePlgSellables().set(componentItem.identifier, plgItems);
                    }
                });
            }
        });
    }

    ngOnInit(): void {
        this.minimalMode = this.responsiveService.mobile() && this.menuModel.itemHasCompositeProductInSizes()(this.orderItem().menuItem());

        if (this.orderItem().menuItem().sellableSizeGroup) {
            const sizes: MenuItemSize[] = this.menuModel.itemSizes()(this.orderItem().menuItem());
            this.menuItemSizes.set(sizes ? sizes.map((size: MenuItemSize) => {
                this.menuModel.initialiseItem(size.menuItem);
                this.promoModel.promotedFeatureTag()(this.category(), size.menuItem);
                return size;
            }) : undefined);
            if (this.menuItemSizes()) {
                const size: MenuItemSize = this.menuItemSizes()!.find((touchSize: MenuItemSize) => touchSize.menuItem.sellable.sellableId === this.orderItem().sellable().sellableId);
                if (size && !this.orderModel.menuItemAtLimit()(size.menuItem, 1, 0, this.originalOrderItem()?.sellableQtyOrdered()(size.menuItem.sellable.sellableId))) {
                    this.selectedSize.set(size);
                    this.originalSelectedSize = this.selectedSize();
                }
            }
        }
    }

    public getSizePrice(size: MenuItemSize): number {
        if (OrderModel.SHOW_RELATIVE_PRICING_FOR_SIZES) {
            const price: number = size.menuItem.sellable.defaultPrice - this.originalSelectedSize.menuItem.sellable.defaultPrice;
            return price !== 0 ? price : undefined;
        }
        return size.menuItem.sellable.defaultPrice;
    }

    public getSizeGuestPrice(size: MenuItemSize): number {
        if (OrderModel.SHOW_RELATIVE_PRICING_FOR_SIZES) {
            const price: number = size.menuItem.sellable.defaultGuestPrice - this.originalSelectedSize.menuItem.sellable.defaultGuestPrice;
            return price !== 0 ? price : undefined;
        }
        return size.menuItem.sellable.defaultGuestPrice;
    }

    public isOrderPlgItem(item: OrderItem): boolean {
        return item instanceof OrderPlgItem;
    }

    public asOrderPlgItem(item: OrderItem): OrderPlgItem {
        return <OrderPlgItem>item;
    }

    public showCompositeItem(item: OrderItem): boolean {
        return !this.isOrderPlgItem(item) || !!(<OrderPlgItem>item).plg.sellableLines.length;
    }

    public plgName(item: OrderItem): string {
        return item instanceof OrderPlgItem ? ((<OrderPlgItem>item).plg.externalName || (<OrderPlgItem>item).plg.name) : undefined;
    }

    public async selectSize(size: MenuItemSize): Promise<void> {
        if (size !== this.selectedSize() && size !== this.originalSelectedSize &&
            this.orderModel.menuItemAtLimit()(size.menuItem, 1, 0, this.originalOrderItem()?.sellableQtyOrdered()(size.menuItem.sellable.sellableId))) {
            if (this.menuModel.itemOutOfStock()(size.menuItem)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: this.menuModel.getMenuItemDisplayName(size.menuItem)});
            } else {
                void this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: this.menuModel.getMenuItemDisplayName(size.menuItem)});
            }
            return;
        }

        if (this.orderingContextModel.currentStore().rsaMinimumAge && size.menuItem.sellable.applyRsa && !await this.orderModel.verifyRsa()) {
            return;
        }

        const qtyAvailable: number = this.menuModel.sellableQtyAvailable()(size.menuItem.sellable.sellableId);
        if (this.orderItem().quantity > qtyAvailable) {
            this.orderItem().quantity = qtyAvailable;
        }
        if (this.orderItem().quantity < size.menuItem.sellable.minWebOrderQty) {
            this.orderItem().quantity = size.menuItem.sellable.minWebOrderQty;
        }

        this.selectedSize.set(size);
        this.onSizeSelect.emit(this.selectedSize().menuItem);
    }

    public extraChanged(extraGroup: MenuExtraGroupData): void {
        this.onExtraChange.emit(extraGroup);
    }

    public async selectPlgSellable(plgItem: OrderPlgItem, plgSellable?: MenuPlgSellableData): Promise<void> {
        if (!(plgItem instanceof OrderPlgItem) || (plgSellable && plgItem.matchesSellable()(plgSellable))) {
            return;
        }

        if (plgSellable) {
            if (this.plgSellableAtLimit()(plgSellable)) {
                if (this.menuModel.itemOutOfStock()(plgSellable.menuItem)) {
                    void this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: this.menuModel.getMenuItemDisplayName(plgSellable.menuItem)});
                } else {
                    void this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: this.menuModel.getMenuItemDisplayName(plgSellable.menuItem)});
                }
                return;
            }

            if (this.orderingContextModel.currentStore().rsaMinimumAge && plgSellable.sellable.applyRsa && !await this.orderModel.verifyRsa()) {
                return;
            }

            plgItem.selectSellable(plgSellable.sellable, this.orderCompositeItem());
        } else if (this.responsiveService.mobile()) {
            const newItem: OrderPlgItem = await this.navigationService.createPage("woa-order-item-plg-edit-page", {
                orderItem: this.orderItem(),
                currentItem: <OrderPlgItem>plgItem,
                menuItem: this.orderItem().menuItem()
            });
            if (!newItem || ((<OrderPlgItem>plgItem).sellable() && (<OrderPlgItem>plgItem).sellable().sellableId === newItem.sellable().sellableId)) {
                return;
            }

            this.orderCompositeItem().replaceOrderItem((<OrderPlgItem>plgItem).identifier, newItem);
        }
    }
}
